import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  // Without an explicit timeout, axios waits for the browser default
  // (~120 s) on a stalled request. With single-worker concurrency stalls
  // that can mean a *very* long "loading…" before retry. 25 s is long
  // enough for legitimate heavy responses (LLM streaming first byte,
  // GridFS image downloads) and short enough that a genuinely starved
  // request fails fast and triggers the courses-retry path.
  timeout: 25000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("uvc_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem("uvc_token");
      localStorage.removeItem("uvc_user");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
      return Promise.reject(err);
    }

    // ─── Transparent retry on transient infra errors ────────────────────
    // 502 / 503 / 504 + network drops + timeouts almost always mean the
    // Emergent ingress couldn't reach our backend for a moment (cold start,
    // worker restart, brief load spike). Failing the request straight to
    // the UI surfaces a scary "Server hiccup" card for what is usually
    // a sub-second blip. We auto-retry up to 3× with exponential backoff
    // (400 ms, 1 s, 2.2 s) BEFORE the request resolves — the user never
    // sees an error unless every attempt fails.
    //
    // Non-idempotent verbs (POST, PATCH, PUT, DELETE) are NOT retried by
    // default to avoid double-writes; GETs are always safe to retry.
    const cfg = err?.config || {};
    const status = err?.response?.status;
    const code = err?.code;
    const isTransient = (
      status === 502 || status === 503 || status === 504 ||
      code === "ECONNABORTED" || code === "ERR_NETWORK" ||
      (!err.response && err.message && /Network Error|timeout/i.test(err.message))
    );
    const method = (cfg.method || "get").toLowerCase();
    const isIdempotent = method === "get" || method === "head" || cfg._retryNonIdempotent === true;
    cfg._retryCount = cfg._retryCount || 0;
    const MAX_RETRIES = 3;

    if (isTransient && isIdempotent && cfg._retryCount < MAX_RETRIES) {
      cfg._retryCount += 1;
      const delay = 400 * Math.pow(2.2, cfg._retryCount - 1); // 400, 880, 1936 ms
      await new Promise((r) => setTimeout(r, delay));
      // eslint-disable-next-line no-console
      console.info(`[api] silent retry ${cfg._retryCount}/${MAX_RETRIES} for ${method.toUpperCase()} ${cfg.url} after status=${status || code}`);
      return api(cfg);
    }
    return Promise.reject(err);
  }
);

export function getUser() {
  try {
    const raw = localStorage.getItem("uvc_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setAuth(token, user) {
  localStorage.setItem("uvc_token", token);
  localStorage.setItem("uvc_user", JSON.stringify(user));
}

export function clearAuth() {
  localStorage.removeItem("uvc_token");
  localStorage.removeItem("uvc_user");
}

export function getToken() {
  return localStorage.getItem("uvc_token");
}
